import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AuthContext } from "../../context/auth";
import { useActiveAccount } from "../../context/active-account";
import { ViewModeContext } from "../../context/view-mode";
import { nextRequiredOnboardingStep, onboardingCountry } from "../../shared/onboarding";
import { markTutorialSeen } from "../../shared/services";
import { useTranslation } from "../../lib/use-translation";
import { fallbackCopy } from "./copy";
import { isTourEligible } from "./eligibility";
import { findTarget } from "./findTarget";
import { IDLE, tourReducer } from "./machine";
import { mergeSeen, readSeen, writeSeen } from "./seenStore";
import { TOURS } from "./tours";
import type { TourId } from "./types";
import TourIntroDialog from "./TourIntroDialog";
import TourStepPopover from "./TourStepPopover";
import TourFinishSnackbar from "./TourFinishSnackbar";

type TourContextValue = {
  activeTourId: TourId | null;
  startTour: (id: TourId, opts?: { force?: boolean }) => void;
  isSeen: (id: TourId) => boolean;
  /** Internal: pages ask for their tour through useTourTrigger. */
  requestAuto: (id: TourId, ready: boolean) => void;
};

// Outside a provider (most existing tests) every call is a no-op.
const TourContext = createContext<TourContextValue>({
  activeTourId: null,
  startTour: () => {},
  isSeen: () => true,
  requestAuto: () => {},
});

export const useTour = () => useContext(TourContext);

/** Asks for `id` to auto-start once `ready`; the provider applies every rule. */
export const useTourTrigger = (id: TourId, { ready }: { ready: boolean }) => {
  const { requestAuto } = useContext(TourContext);
  useEffect(() => {
    requestAuto(id, ready);
  }, [id, ready, requestAuto]);
};

const pathOf = (route: string) => route.split("?")[0];

/**
 * Runs the Hub's guided tours: decides when one may auto-start, walks its
 * steps (navigating when a step lives on another route), and records it
 * seen on the server when the person finishes or dismisses it.
 */
export const TourProvider = ({ children }: { children: ReactNode }) => {
  const { user, isImpersonatingFromAdmin } = useContext(AuthContext);
  const { isAdminViewingAsUser } = useContext(ViewModeContext);
  const { isTeamMode, isDeveloperMode, activeTeam, activeGroupId } = useActiveAccount();
  // noSsr: read synchronously, or the first render says "wide" and a tour
  // could start on a phone before the query settles.
  const isNarrow = useMediaQuery("(max-width:1000px)", { noSsr: true });
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("components/tour", "tour");
  const tr = useCallback((node: string): string => t(node, fallbackCopy(node)), [t]);

  const [state, dispatch] = useReducer(tourReducer, IDLE);
  const [localSeen, setLocalSeen] = useState<Set<TourId>>(() => new Set());
  const [target, setTarget] = useState<HTMLElement | null>(null);
  // A replayed tour was already seen: closing it must not POST again.
  const forcedRef = useRef(false);
  // The route the current step lives on; leaving it means the user navigated away.
  const stepPathRef = useRef<string | null>(null);
  // A step-driven navigate() that hasn't landed yet: not the user leaving.
  const pendingPathRef = useRef<string | null>(null);
  // Where the intro opened; leaving it before "Take the tour" dismisses.
  const introPathRef = useRef<string | null>(null);
  // Whether this run has shown any step, so an all-skipped tour ends quietly.
  const shownAnyRef = useRef(false);

  const userId: string | undefined = user?.id ?? user?.userId;

  useEffect(() => {
    setLocalSeen(userId ? readSeen(userId) : new Set());
    dispatch({ type: "reset" });
  }, [userId]);

  // Switching account mid-tour ends it: its copy and anchors are for the old one.
  const accountKey = isDeveloperMode ? "developer" : isTeamMode ? `team:${activeGroupId ?? ""}` : "individual";
  const accountKeyRef = useRef(accountKey);
  useEffect(() => {
    if (accountKeyRef.current === accountKey) return;
    accountKeyRef.current = accountKey;
    dispatch({ type: "reset" });
  }, [accountKey]);

  const seen = useMemo(() => mergeSeen(user?.tutorialsSeen, localSeen), [user?.tutorialsSeen, localSeen]);
  const activeTourId = state.phase === "intro" || state.phase === "step" ? state.tourId : null;

  const markSeen = useCallback(
    (id: TourId) => {
      if (!userId || forcedRef.current) return;
      setLocalSeen((prev) => {
        const next = new Set(prev).add(id);
        writeSeen(userId, next);
        return next;
      });
      markTutorialSeen(userId, id).catch((err) => Sentry.captureException(err, { tags: { tour: id } }));
    },
    [userId],
  );

  const startTour = useCallback((id: TourId, { force = false }: { force?: boolean } = {}) => {
    forcedRef.current = force;
    shownAnyRef.current = false;
    dispatch({ type: "start", tourId: id });
  }, []);

  const requestAuto = useCallback(
    (id: TourId, ready: boolean) => {
      if (!ready) return;
      const owesOnboarding =
        nextRequiredOnboardingStep(user, onboardingCountry(user ?? undefined), { isImpersonatingFromAdmin }) !==
        null;
      const eligible = isTourEligible({
        tourId: id,
        seen,
        activeTourId,
        user: user && userId ? { id: userId, createdAt: user.createdAt, role: user.role, userType: user.userType } : null,
        owesOnboarding,
        isAdminViewing: isAdminViewingAsUser || isImpersonatingFromAdmin,
        isNarrow,
        account: isDeveloperMode
          ? { kind: "developer" }
          : isTeamMode
            ? { kind: "team", teamOwnerUserId: activeTeam?.ownerUserId }
            : { kind: "individual" },
      });
      if (eligible) startTour(id);
    },
    [
      user,
      userId,
      seen,
      activeTourId,
      isAdminViewingAsUser,
      isImpersonatingFromAdmin,
      isNarrow,
      isDeveloperMode,
      isTeamMode,
      activeTeam?.ownerUserId,
      startTour,
    ],
  );

  const tour = state.phase === "idle" ? null : TOURS[state.tourId];
  const step = state.phase === "step" && tour ? tour.steps[state.index] : null;
  // The skip path runs in a promise callback; read the current markSeen.
  const markSeenRef = useRef(markSeen);
  markSeenRef.current = markSeen;

  // Resolve the current step's target, navigating first if it lives elsewhere.
  useEffect(() => {
    setTarget(null);
    if (!step || !tour) {
      stepPathRef.current = null;
      return;
    }
    if (step.route && `${location.pathname}${location.search}` !== step.route) {
      stepPathRef.current = pathOf(step.route);
      pendingPathRef.current = pathOf(step.route);
      navigate(step.route);
    } else {
      stepPathRef.current = location.pathname;
    }
    const search = findTarget(step.target);
    search.promise.then((el) => {
      if (el) {
        shownAnyRef.current = true;
        setTarget(el);
        return;
      }
      Sentry.addBreadcrumb({ category: "tour", message: `skipped missing target ${step.target}` });
      if (tour.steps.indexOf(step) < tour.steps.length - 1) {
        dispatch({ type: "next", stepCount: tour.steps.length });
        return;
      }
      // Skipping the last step still ends the tour: mark it seen so it
      // can't auto-start again, and skip the outro if nothing was shown.
      markSeenRef.current(tour.id);
      dispatch(shownAnyRef.current ? { type: "next", stepCount: tour.steps.length } : { type: "dismiss" });
    });
    return search.cancel;
    // location is read only on entering a step; leaving is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, tour]);

  const introTourId = state.phase === "intro" ? state.tourId : null;
  useEffect(() => {
    introPathRef.current = introTourId ? location.pathname : null;
    // Only on entering the intro; leaving is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introTourId]);

  // Leaving the tour's page by any other means (Back, a typed URL) dismisses.
  useEffect(() => {
    if (state.phase === "intro") {
      if (introPathRef.current && location.pathname !== introPathRef.current) {
        markSeen(state.tourId);
        dispatch({ type: "dismiss" });
      }
      return;
    }
    if (state.phase !== "step") return;
    if (pendingPathRef.current) {
      if (location.pathname !== pendingPathRef.current) return; // still on the way
      pendingPathRef.current = null;
    }
    if (stepPathRef.current && location.pathname !== stepPathRef.current) {
      markSeen(state.tourId);
      dispatch({ type: "dismiss" });
    }
  }, [location.pathname, state, markSeen]);

  const dismiss = useCallback(() => {
    if (activeTourId) markSeen(activeTourId);
    dispatch({ type: "dismiss" });
  }, [activeTourId, markSeen]);

  const next = useCallback(() => {
    if (state.phase !== "step" || !tour) return;
    if (state.index + 1 >= tour.steps.length) markSeen(state.tourId);
    dispatch({ type: "next", stepCount: tour.steps.length });
  }, [state, tour, markSeen]);

  const value = useMemo<TourContextValue>(
    () => ({ activeTourId, startTour, isSeen: (id) => seen.has(id), requestAuto }),
    [activeTourId, startTour, seen, requestAuto],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {tour && state.phase === "intro" && (
        <TourIntroDialog
          open
          tourId={tour.id}
          title={tr(tour.intro.titleKey)}
          body={tr(tour.intro.bodyKey)}
          takeTourLabel={tr("common.takeTour")}
          closeLabel={tr("common.close")}
          onBegin={() => dispatch({ type: "begin" })}
          onDismiss={dismiss}
        />
      )}
      {tour && step && target && state.phase === "step" && (
        <TourStepPopover
          key={`${tour.id}-${state.index}`}
          target={target}
          title={tr(step.titleKey)}
          body={tr(step.bodyKey)}
          stepLabel={tr("common.stepOf")
            .replace("{{n}}", String(state.index + 1))
            .replace("{{total}}", String(tour.steps.length))}
          nextLabel={tr(state.index + 1 >= tour.steps.length ? "common.done" : "common.next")}
          closeLabel={tr("common.close")}
          placement={step.placement ?? "right"}
          onNext={next}
          onDismiss={dismiss}
        />
      )}
      <TourFinishSnackbar
        open={state.phase === "finished"}
        message={tour ? tr(tour.outroKey) : ""}
        okLabel={tr("common.ok")}
        onClose={() => dispatch({ type: "closeSnackbar" })}
      />
    </TourContext.Provider>
  );
};
